<script setup lang="ts">
/**
 * The chat header's assistant selector. Loads the caller's visible
 * assistants once on mount and hands them to the shared AssistantChip, so
 * this control looks and behaves exactly like the one in the Hub's ask bar.
 *
 * It used to be a native `<select>` — unstyleable, rendering OS chrome and
 * an OS-blue highlighted option in the middle of a frosted-glass surface.
 *
 * Uses `/api/assistants/visible` so the list respects each assistant's
 * `acl_scope` — a user with access only to General must never see the other
 * slugs listed here. The Hub filters the same way; the two must match.
 *
 * Reloads when the dev-user picker switches identity: without the dev
 * user the backend answers for its default identity, not the one chosen.
 *
 * Renders nothing until loaded, and nothing at all when the caller has no
 * assistants — the chip itself handles the single-assistant case by going
 * static rather than offering a menu with one entry.
 */
import { onMounted, ref, watch } from "vue";
import { listVisibleAssistants, type Assistant } from "../api/conversations";
import AssistantChip from "./AssistantChip.vue";

const props = defineProps<{ modelValue: string; devUser?: string }>();
const emit = defineEmits<{ (e: "update:modelValue", slug: string): void }>();

const items = ref<Assistant[]>([]);
const loaded = ref(false);

async function load(): Promise<void> {
  items.value = await listVisibleAssistants(props.devUser || undefined);
  loaded.value = true;
  if (!props.modelValue && items.value.length > 0) {
    emit("update:modelValue", items.value[0].slug);
  }
}
onMounted(load);
watch(() => props.devUser, load);
</script>

<template>
  <AssistantChip
    v-if="loaded && items.length > 0"
    :assistants="items"
    :model-value="modelValue"
    max-width="18rem"
    @update:model-value="emit('update:modelValue', $event)"
  />
</template>
